import type { SubscriptionStatus } from 'src/features/subscription/api';

import { useQuery } from '@tanstack/react-query';
import { useMemo, useEffect, useCallback } from 'react';

import { useAuth } from 'src/features/auth';
import { subscriptionService } from 'src/features/subscription/api';

import { getAuthFromStorage } from 'src/lib/auth-storage';

import { subscriptionQueryKeys } from './query-keys';

interface UserWithSubscription {
  isPro?: boolean;
  proEndDateUtc?: string | null;
  freeTrialCount?: number;
  isActive?: boolean;
}

const DEFAULT_STATUS: SubscriptionStatus = {
  isPro: false,
  isActive: false,
  freeTrialCount: 0,
  proEndDateUtc: null,
};

const hasSubscriptionStatusShape = (value: unknown): value is SubscriptionStatus => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.isPro === 'boolean' &&
    typeof candidate.isActive === 'boolean' &&
    typeof candidate.freeTrialCount === 'number' &&
    (candidate.proEndDateUtc === null || typeof candidate.proEndDateUtc === 'string')
  );
};

const normalizeStatusResponse = (response: unknown): SubscriptionStatus => {
  if (hasSubscriptionStatusShape(response)) {
    return { ...DEFAULT_STATUS, ...response };
  }

  if (!response || typeof response !== 'object') {
    return DEFAULT_STATUS;
  }

  const responseRecord = response as Record<string, unknown>;
  if (hasSubscriptionStatusShape(responseRecord.data)) {
    return { ...DEFAULT_STATUS, ...responseRecord.data };
  }

  return DEFAULT_STATUS;
};

const getStatusFromUser = (user: unknown): SubscriptionStatus | null => {
  if (!user || typeof user !== 'object') {
    return null;
  }

  const userWithSubscription = user as UserWithSubscription;
  if (typeof userWithSubscription.isPro !== 'boolean') {
    return null;
  }

  return {
    isPro: userWithSubscription.isPro,
    isActive: Boolean(userWithSubscription.isActive),
    freeTrialCount: Number(userWithSubscription.freeTrialCount) || 0,
    proEndDateUtc: userWithSubscription.proEndDateUtc ?? null,
  };
};

const getStatusFromStorage = (): SubscriptionStatus => {
  const auth = getAuthFromStorage();
  if (!auth?.user || typeof auth.user !== 'object') {
    return DEFAULT_STATUS;
  }

  const userWithSubscription = auth.user as UserWithSubscription;
  if (typeof userWithSubscription.isPro !== 'boolean') {
    return DEFAULT_STATUS;
  }

  return {
    isPro: userWithSubscription.isPro,
    isActive: Boolean(userWithSubscription.isActive),
    freeTrialCount: Number(userWithSubscription.freeTrialCount) || 0,
    proEndDateUtc: userWithSubscription.proEndDateUtc ?? null,
  };
};

export const useSubscriptionStatus = () => {
  const { isAuthenticated, user } = useAuth();

  const fallbackStatus = useMemo(
    () => getStatusFromUser(user) ?? getStatusFromStorage(),
    [user]
  );

  const { data, isFetching, refetch } = useQuery({
    queryKey: subscriptionQueryKeys.status(),
    queryFn: async (): Promise<SubscriptionStatus> =>
      normalizeStatusResponse(await subscriptionService.getStatus()),
    enabled: isAuthenticated,
    // Placeholder, not initial data: initial data counts as fresh for `staleTime`, so the
    // server was never asked for a minute after sign-in. And the signed-in user carries no
    // `isActive`, so a renewing subscriber was shown "Pro Cancelled" without a Cancel button.
    placeholderData: fallbackStatus,
    staleTime: 60_000,
  });

  const status = isAuthenticated ? data ?? fallbackStatus : DEFAULT_STATUS;

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const auth = getAuthFromStorage();
    if (!auth?.user) {
      return;
    }

    localStorage.setItem(
      'auth',
      JSON.stringify({
        ...auth,
        user: {
          ...auth.user,
          isPro: status.isPro,
          isActive: status.isActive,
          proEndDateUtc: status.proEndDateUtc,
          freeTrialCount: status.freeTrialCount,
        },
      })
    );
  }, [
    isAuthenticated,
    status.freeTrialCount,
    status.isActive,
    status.isPro,
    status.proEndDateUtc,
  ]);

  const refreshSubscription = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) {
      return;
    }
    await refetch();
  }, [isAuthenticated, refetch]);

  return {
    isPro: status.isPro,
    isActive: status.isActive,
    freeTrialCount: status.freeTrialCount,
    proEndDate: status.proEndDateUtc ? new Date(status.proEndDateUtc) : null,
    loading: isAuthenticated ? isFetching : false,
    refreshSubscription,
  };
};
